import { Org } from '../locations'
import type { EntityId } from '../shared'
import { OrderStatus } from './Order'
import { OtcProductStatus } from './OtcProducts'
import { RxFillRequestStatus } from './RxFillRequest'
import type { OrderTriageReasons } from './Triage'

export enum OrderSplitStatus {
  Pending = 'pending',
  Complete = 'complete',
  Failed = 'failed',
}

// How long (ms) a pending split stays in flight while no split-out order is known yet; after that it is stale
export const SPLIT_FLIGHT_TIME_LIMIT = 10 * 60 * 1000

// One entry per split requested on the original order
export interface OrderSplitInput {
  coreFillRequestToken: string
  status: OrderSplitStatus
  targetLocationId: EntityId
  rxFillRequestIds: EntityId[]
  otcProductIds: EntityId[]
  requestedBy: EntityId
  requestedAt: Date
  completedAt?: Date
  orderId?: EntityId
  coreOrderId?: number
}

// Eligibility rule shared by the mutation and the UI
export const splittableRxFillRequestStatuses: string[] = [RxFillRequestStatus.PV1, RxFillRequestStatus.Fill]
export const splittableOtcProductStatuses: string[] = [OtcProductStatus.Processing]

export const isRxFillRequestSplittable = (status?: string): boolean =>
  !!status && splittableRxFillRequestStatuses.includes(status)

export const isOtcProductSplittable = (status?: string): boolean =>
  !!status && splittableOtcProductStatuses.includes(status)

// A split entry plus the status of the split-out order when the caller loaded it
export type SplitFlightInput = Pick<
  OrderSplitInput,
  'status' | 'requestedAt' | 'orderId' | 'rxFillRequestIds' | 'otcProductIds'
> & {
  splitOutOrderStatus?: string | null
}

// A split is in flight while the items are still moving to the split-out order:
// No split-out order exists (within the time limit), or there is one that is not Cancelled
export const isSplitInFlight = (split: SplitFlightInput, now: Date = new Date()): boolean => {
  if (split.status !== OrderSplitStatus.Pending) return false
  // An unknown split-out order status counts as live
  if (split.orderId) return split.splitOutOrderStatus !== OrderStatus.Cancelled
  return new Date(split.requestedAt).getTime() > now.getTime() - SPLIT_FLIGHT_TIME_LIMIT
}

// Ids of every Rx fill and OTC in split flight; they cannot be split again
export const itemIdsInSplitFlight = (splits: SplitFlightInput[] = [], now: Date = new Date()): Set<string> => {
  const ids = new Set<string>()
  for (const split of splits) {
    if (!isSplitInFlight(split, now)) continue
    for (const id of [...split.rxFillRequestIds, ...split.otcProductIds]) ids.add(String(id))
  }
  return ids
}

// The location fields the split target filter reads
export type SplitTargetLocation = {
  _id: EntityId
  active?: boolean
  isThirdParty?: boolean
  org?: string | null
}

export type SplitTargetContext = {
  selectionHasRx: boolean
}

// LetsGetChecked runs the only non-pharmacy locations, so Rx fills can never go there
export const isSplitTargetLocation = (
  location: SplitTargetLocation,
  { selectionHasRx }: SplitTargetContext,
): boolean => {
  if (!location.active || location.isThirdParty) return false
  const orgIsLgc = (location.org ?? Org.Truepill) === Org.LetsGetChecked
  return !(selectionHasRx && orgIsLgc)
}

// An OTC-only selection may cancel the original along with its last items: an Rx+OTC order whose Rx fills
// were cancelled can reach an OTC-only location no other way. Any other selection must leave a live item behind
export const splitMayEmptyOriginal = ({ selectionHasRx }: SplitTargetContext): boolean => !selectionHasRx

// The give-up triage is found again by this prefix, so no other triage message may start with it.
// It also avoids the duplicate-order wording ResolveTriage matches on.
export const SPLIT_GIVE_UP_TRIAGE_PREFIX = 'Split from order '

export const splitGiveUpTriageMessage = (originalCoreOrderId: number): string =>
  `${SPLIT_GIVE_UP_TRIAGE_PREFIX}${originalCoreOrderId} did not finish: its items are still live on the original`

// Triages store the reason's enum key, not its value
const OOS_TRIAGE_REASON: keyof typeof OrderTriageReasons = 'OOS'

// The triage fields the split resolution reads, as both the order document and the UI hold them
export type SplitResolvableTriage = {
  reason?: string | null
  fillId?: EntityId | null
  endDate?: Date | string | null
}

// A split resolves the original's open order level OOS triages. Fill level ones stay with their fill:
// the fill's own cancel closes them when it moves, and a fill that stays may still be out of stock.
export const isTriageResolvedBySplit = (triage: SplitResolvableTriage): boolean =>
  !triage.endDate && !triage.fillId && triage.reason === OOS_TRIAGE_REASON
